import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  IndianRupee, Clock, CheckCheck, RefreshCw,
  Building2, Phone, Mail, ArrowUpRight, X,
  FileText, Download
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

export default function AdminPayouts() {
  const { token } = useAuth();
  const { colors } = useTheme();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | success
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [manualModalPayout, setManualModalPayout] = useState(null);
  const [manualPaymentMode, setManualPaymentMode] = useState('UPI');
  const [manualTxnId, setManualTxnId] = useState('');
  const [editableUpiId, setEditableUpiId] = useState('');

  const downloadInvoice = async (txnId, invoiceNo) => {
    setDownloadingId(txnId);
    try {
      const res = await axios.get(`${API}/wallet/payouts/${txnId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TurfX_Payout_Invoice_${invoiceNo || txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Invoice download failed:', err);
      alert('Failed to download payout invoice PDF. Make sure the payout has been successfully approved.');
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/admin/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWithdrawals(res.data);
    } catch (err) {
      console.error('Failed to fetch withdrawals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePay = async (txnId, upiOverride) => {
    if (!window.confirm('Are you sure you want to approve and process this payout via RazorpayX?')) return;
    setProcessingId(txnId);
    try {
      const res = await axios.post(
        `${API}/wallet/admin/withdrawals/${txnId}/pay`,
        { upiId: upiOverride || editableUpiId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data?.msg || 'Payout processed successfully!');
      fetchWithdrawals();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.msg || 'Failed to process payout.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleManualPaySubmit = async () => {
    if (!manualModalPayout) return;
    setProcessingId(manualModalPayout.id);
    try {
      const res = await axios.post(
        `${API}/wallet/admin/withdrawals/${manualModalPayout.id}/manual-pay`,
        {
          paymentMode: manualPaymentMode,
          transactionId: manualTxnId,
          upiId: editableUpiId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data?.msg || 'Manual payout recorded successfully!');
      setManualModalPayout(null);
      setManualTxnId('');
      fetchWithdrawals();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.msg || 'Failed to record manual payout.');
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = withdrawals.filter(w => {
    if (filter === 'pending') return w.status === 'pending';
    if (filter === 'success') return w.status === 'success';
    return true;
  });

  const totalRequested = withdrawals.reduce((sum, w) => sum + w.amount, 0);
  const pendingAmount = withdrawals.filter(w => w.status === 'pending').reduce((sum, w) => sum + w.amount, 0);
  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;
  const completedAmount = withdrawals.filter(w => w.status === 'success').reduce((sum, w) => sum + w.amount, 0);
  const completedCount = withdrawals.filter(w => w.status === 'success').length;

  const stats = [
    { label: 'Pending Payouts', value: `₹${pendingAmount.toLocaleString()}`, count: `${pendingCount} requests`, icon: <Clock size={20} />, color: '#f59e0b', bg: '#fef3c7' },
    { label: 'Completed Payouts', value: `₹${completedAmount.toLocaleString()}`, count: `${completedCount} paid`, icon: <CheckCheck size={20} />, color: '#10b981', bg: '#d1fae5' },
    { label: 'Total Requested', value: `₹${totalRequested.toLocaleString()}`, count: `${withdrawals.length} total`, icon: <IndianRupee size={20} />, color: '#3b82f6', bg: '#eff6ff' },
  ];

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '700' }}>
      Loading payout requests...
    </div>
  );

  return (
    <div style={{ padding: '0.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>Payout Requests</h2>
          <p style={{ color: '#374151', fontSize: '0.9rem', fontWeight: '600' }}>
            Review and process wallet withdrawals requested by Turf Owners.
          </p>
        </div>
        <button onClick={fetchWithdrawals} style={btnSecondary}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem', marginBottom: '2rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</span>
              <div style={{ background: s.bg, color: s.color, padding: '8px', borderRadius: '10px' }}>{s.icon}</div>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600' }}>{s.count}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', alignItems: 'center' }}>
        {[
          { id: 'all', label: `All Requests (${withdrawals.length})` },
          { id: 'pending', label: `Pending (${pendingCount})` },
          { id: 'success', label: `Completed (${completedCount})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            style={{
              padding: '8px 20px', borderRadius: '10px', border: '1.5px solid',
              borderColor: filter === t.id ? '#084734' : '#EEF2E6',
              background: filter === t.id ? '#084734' : 'white',
              color: filter === t.id ? '#CEF17B' : '#374151',
              fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead style={{ background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6' }}>
              <tr>
                {['Request Date', 'Vendor / Partner', 'Bank Account Details', 'Amount', 'Status', 'Actions'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '700' }}>
                    No payout requests found
                  </td>
                </tr>
              ) : (
                filtered.map((w) => {
                  const meta = w.metadata || {};
                  return (
                    <tr 
                      key={w.id} 
                      onClick={() => {
                        setSelectedPayout(w);
                        setEditableUpiId(w.metadata?.upiId || '');
                      }}
                      style={{ borderBottom: '1px solid #EEF2E6', background: 'white', cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <td style={tdStyle}>
                        <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0f172a' }}>
                          {w.createdAt ? new Date(w.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#374151' }}>
                          {w.createdAt ? new Date(w.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.85rem' }}>{w.userName || 'Unknown Vendor'}</div>
                        <div style={{ fontSize: '0.72rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                          <Phone size={12} /> {w.userPhone || '—'}
                        </div>
                        {w.userEmail && (
                          <div style={{ fontSize: '0.72rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                            <Mail size={12} /> {w.userEmail}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Building2 size={20} color="#084734" />
                          <div>
                            {meta.upiId && (
                              <div style={{ fontWeight: '850', fontSize: '0.85rem', color: '#16a34a' }}>
                                UPI ID: {meta.upiId}
                              </div>
                            )}
                            <div style={{ fontWeight: '700', fontSize: '0.82rem', color: '#0f172a', marginTop: '2px' }}>
                              Holder: {meta.accountHolderName || w.userName || '—'}
                            </div>
                            {(meta.accountNumber || meta.bankName) && (
                              <div style={{ fontSize: '0.75rem', color: '#374151', marginTop: '2px' }}>
                                {meta.bankName || 'Bank'} A/c: {meta.accountNumber || '—'}
                              </div>
                            )}
                            {meta.ifscCode && (
                              <div style={{ fontSize: '0.7rem', color: '#084734', fontWeight: '700', marginTop: '2px' }}>
                                IFSC: {meta.ifscCode}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '900', fontSize: '1rem', color: '#0f172a' }}>
                          ₹{w.amount.toLocaleString()}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          background: w.status === 'success' ? '#D1FAE5' : w.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                          color: w.status === 'success' ? '#065F46' : w.status === 'pending' ? '#92400E' : '#DC2626',
                          padding: '4px 12px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '800',
                          textTransform: 'uppercase'
                        }}>
                          {w.status || 'PENDING'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {w.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditableUpiId(w.metadata?.upiId || '');
                                handlePay(w.id, w.metadata?.upiId);
                              }}
                              disabled={processingId === w.id}
                              style={btnPay}
                              title="Process auto payout via PayU"
                            >
                              <ArrowUpRight size={14} /> {processingId === w.id ? 'Paying...' : 'PayU Auto'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setManualModalPayout(w);
                                setEditableUpiId(w.metadata?.upiId || '');
                                setManualPaymentMode('UPI');
                                setManualTxnId('');
                              }}
                              disabled={processingId === w.id}
                              style={{ ...btnPay, background: '#1e293b', color: '#f8fafc' }}
                              title="Record manual payout (UPI, Bank Transfer, Cash)"
                            >
                              Manual Pay
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#10b981', fontWeight: '800', fontSize: '0.8rem' }}>✓ Settled</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadInvoice(w.id, w.metadata?.invoice_number);
                              }}
                              disabled={downloadingId === w.id}
                              style={btnInvoice}
                              title="Download Official Payout Invoice (PDF)"
                            >
                              <FileText size={13} /> {downloadingId === w.id ? 'PDF...' : 'Invoice'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Details Modal */}
      {selectedPayout && (
        <div style={modalOverlay} onClick={() => setSelectedPayout(null)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616', margin: 0 }}>Payout Request Details</h3>
              <X size={20} onClick={() => setSelectedPayout(null)} style={{ cursor: 'pointer', color: '#374151' }} />
            </div>

            {/* Top overview card */}
            <div style={{ textAlign: 'center', padding: '1.25rem', background: '#F8FAF7', borderRadius: '16px', marginBottom: '1.5rem', border: '1px solid #EEF2E6' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', color: '#374151', marginBottom: '4px', letterSpacing: '0.5px' }}>
                Requested Amount
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#084734' }}>
                ₹{selectedPayout.amount.toLocaleString()}
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{
                  background: selectedPayout.status === 'success' ? '#D1FAE5' : selectedPayout.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                  color: selectedPayout.status === 'success' ? '#065F46' : selectedPayout.status === 'pending' ? '#92400E' : '#DC2626',
                  padding: '4px 12px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800', textTransform: 'uppercase'
                }}>
                  {selectedPayout.status || 'PENDING'}
                </span>
              </div>
            </div>

            {/* Detailed properties */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: '800', color: '#084734', fontSize: '0.9rem', marginBottom: '4px', borderBottom: '1px solid #EEF2E6', paddingBottom: '4px' }}>
                Vendor Profile
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Partner / Host Name</span>
                <span style={detailValue}>{selectedPayout.userName || 'Unknown Vendor'}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Phone Number</span>
                <span style={detailValue}>{selectedPayout.userPhone || '—'}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Email Address</span>
                <span style={detailValue}>{selectedPayout.userEmail || '—'}</span>
              </div>

              <div style={{ fontWeight: '800', color: '#084734', fontSize: '0.9rem', marginTop: '0.5rem', marginBottom: '4px', borderBottom: '1px solid #EEF2E6', paddingBottom: '4px' }}>
                Payment & Payout Target
              </div>
              
              {/* Editable UPI ID */}
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '800', color: '#084734', marginBottom: '4px' }}>
                  Target UPI ID (Editable by Admin)
                </label>
                <input
                  type="text"
                  value={editableUpiId}
                  onChange={e => setEditableUpiId(e.target.value)}
                  placeholder="e.g. vendor@upi"
                  disabled={selectedPayout.status !== 'pending'}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #8ECA25', fontSize: '0.9rem', fontWeight: '800', color: '#084734', background: selectedPayout.status === 'pending' ? '#F8FAF7' : '#f3f4f6' }}
                />
              </div>

              <div style={detailRow}>
                <span style={detailLabel}>Account Holder Name</span>
                <span style={detailValue}>{selectedPayout.metadata?.accountHolderName || '—'}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Bank Name</span>
                <span style={detailValue}>{selectedPayout.metadata?.bankName || '—'}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Bank Account Number</span>
                <span style={{ ...detailValue, fontFamily: 'monospace' }}>{selectedPayout.metadata?.accountNumber || '—'}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>IFSC Code</span>
                <span style={{ ...detailValue, fontFamily: 'monospace' }}>{selectedPayout.metadata?.ifscCode || '—'}</span>
              </div>

              <div style={{ fontWeight: '800', color: '#084734', fontSize: '0.9rem', marginTop: '0.5rem', marginBottom: '4px', borderBottom: '1px solid #EEF2E6', paddingBottom: '4px' }}>
                Metadata & Settlement
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Requested Time</span>
                <span style={detailValue}>
                  {selectedPayout.createdAt ? new Date(selectedPayout.createdAt).toLocaleString('en-IN') : '—'}
                </span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Transaction Reference ID</span>
                <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedPayout.id}</span>
              </div>
              {selectedPayout.metadata?.invoice_number && (
                <div style={detailRow}>
                  <span style={detailLabel}>Invoice Number</span>
                  <span style={{ ...detailValue, color: '#16a34a', fontWeight: '800', fontFamily: 'monospace' }}>
                    {selectedPayout.metadata.invoice_number}
                  </span>
                </div>
              )}
              {selectedPayout.metadata?.payment_mode && (
                <div style={detailRow}>
                  <span style={detailLabel}>Payment Mode</span>
                  <span style={{ ...detailValue, color: '#084734', fontWeight: '800' }}>{selectedPayout.metadata.payment_mode}</span>
                </div>
              )}
              {(selectedPayout.metadata?.transaction_id || selectedPayout.metadata?.razorpay_utr) && (
                <div style={detailRow}>
                  <span style={detailLabel}>Transaction / UTR ID</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace' }}>
                    {selectedPayout.metadata.transaction_id || selectedPayout.metadata.razorpay_utr}
                  </span>
                </div>
              )}

              {selectedPayout.metadata?.payout_error && (
                <div style={{ marginTop: '0.75rem', padding: '12px 16px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '12px', color: '#DC2626' }}>
                  <div style={{ fontWeight: '800', fontSize: '0.78rem', textTransform: 'uppercase', marginBottom: '4px' }}>Last Payout Error</div>
                  <div style={{ fontSize: '0.82rem', fontWeight: '600', lineHeight: 1.4 }}>{selectedPayout.metadata.payout_error}</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              {selectedPayout.status === 'success' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadInvoice(selectedPayout.id, selectedPayout.metadata?.invoice_number);
                  }}
                  disabled={downloadingId === selectedPayout.id}
                  style={{ ...btnPay, width: '100%', justifyContent: 'center', padding: '12px', background: '#084734', color: '#CEF17B' }}
                >
                  <FileText size={16} /> {downloadingId === selectedPayout.id ? 'Downloading PDF...' : 'Download Payout Invoice (PDF)'}
                </button>
              )}
              
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedPayout.status === 'pending' && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePay(selectedPayout.id, editableUpiId);
                        setSelectedPayout(null);
                      }}
                      disabled={processingId === selectedPayout.id}
                      style={{ ...btnPay, flex: 1, justifyContent: 'center', padding: '12px' }}
                    >
                      <ArrowUpRight size={16} /> PayU Auto
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setManualModalPayout(selectedPayout);
                        setSelectedPayout(null);
                      }}
                      disabled={processingId === selectedPayout.id}
                      style={{ ...btnPay, background: '#1e293b', color: '#f8fafc', flex: 1, justifyContent: 'center', padding: '12px' }}
                    >
                      Manual Pay
                    </button>
                  </>
                )}
                <button onClick={() => setSelectedPayout(null)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center', padding: '12px' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Pay Input Modal */}
      {manualModalPayout && (
        <div style={modalOverlay} onClick={() => setManualModalPayout(null)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616', margin: 0 }}>Record Manual Payout</h3>
              <X size={20} onClick={() => setManualModalPayout(null)} style={{ cursor: 'pointer', color: '#374151' }} />
            </div>

            <div style={{ padding: '1.25rem', background: '#F8FAF7', borderRadius: '16px', marginBottom: '1.5rem', border: '1px solid #EEF2E6' }}>
              <div style={{ fontSize: '0.78rem', color: '#374151', fontWeight: '700' }}>Vendor: <strong>{manualModalPayout.userName || 'Vendor'}</strong></div>
              <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#084734', marginTop: '4px' }}>Amount: ₹{manualModalPayout.amount.toLocaleString()}</div>
            </div>

            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Target UPI ID (Editable by Admin)</label>
              <input
                type="text"
                value={editableUpiId}
                onChange={e => setEditableUpiId(e.target.value)}
                placeholder="e.g. vendor@upi"
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #8ECA25', fontSize: '0.9rem', fontWeight: '800', color: '#084734', background: '#F8FAF7' }}
              />
            </div>

            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Payment Mode</label>
              <select
                value={manualPaymentMode}
                onChange={e => setManualPaymentMode(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', fontWeight: '700', background: 'white' }}
              >
                <option value="UPI">UPI Transfer</option>
                <option value="Bank Transfer">Bank Transfer (IMPS / NEFT / RTGS)</option>
                <option value="Cash">Cash Payment</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Transaction ID / UTR Ref Number (Optional)</label>
              <input
                type="text"
                placeholder="e.g. UTR1234567890 or Txn Ref"
                value={manualTxnId}
                onChange={e => setManualTxnId(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', fontWeight: '600' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleManualPaySubmit}
                disabled={processingId === manualModalPayout.id}
                style={{ ...btnPay, flex: 1, justifyContent: 'center', padding: '12px' }}
              >
                {processingId === manualModalPayout.id ? 'Recording...' : 'Confirm & Mark as Paid'}
              </button>
              <button onClick={() => setManualModalPayout(null)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center', padding: '12px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = { background: 'white', padding: '1.5rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' };
const btnSecondary = { background: 'white', color: '#374151', border: '1.5px solid #EEF2E6', padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const btnPay = { background: '#084734', color: '#CEF17B', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: '800', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(8,71,52,0.15)' };
const btnInvoice = { background: '#F0FDF4', color: '#16a34a', border: '1.5px solid #86efac', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' };
const thStyle = { textAlign: 'left', padding: '1rem 1.25rem', fontSize: '0.72rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '1rem 1.25rem', verticalAlign: 'middle' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modalBox   = { background: 'white', borderRadius: '20px', padding: '2rem', width: '480px', maxWidth: '95%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const detailRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid #F3F4F6',
};
const detailLabel = {
  color: '#374151',
  fontWeight: '600',
};
const detailValue = {
  color: '#161616',
  fontWeight: '700',
  textAlign: 'right',
};
